import {
  dateRangeVariable,
  useDateRange,
} from "../../../../../common/components/datePicker/modelsDatePickers";
import { memo, useEffect, useState } from "react";

import { AutocompleteOptionType } from "../../../../../common/models/autocomplete";
import { Box } from "@mui/material";
import { CHART_CONTAINER_HEIGHT } from "../../system-health/common/values";
import DetailedInformationView from "./DetailedInformationView";
import GasLeakEmissionVolumeView from "./GasLeakEmissionVolumeView";
import { IStatisticsResponseModel } from "../models/chart";
import PolarAreaChart from "./PolarAreaChart";
import SimplePaperWrapper from "../../../../../common/components/item/SimplePaperWrapper";
import { TimePeriod } from "../../../../../API";
import isEqual from "lodash/isEqual";
import { useGasLeakEmissionsChart } from "../hooks/useGasLeakEmissionsChart";
import { useGetLocationsCached } from "../../../../customer-settings/tabs/locations/hooks/useGetLocationsCached";
import { useLocationsFilterDropdown } from "../hooks/useLocationsFilterDropdown";

export enum GasLeakContainerSteps {
  EmissionVolumeChart,
  DetailedLocaleInformation,
  PolarAreaChart,
}

const GasLeakChartContainerComponent = (): JSX.Element => {
  const [activeStep, setActiveStep] = useState(0);
  const [deviceData, setDeviceData] = useState<IStatisticsResponseModel | null>(
    null
  );

  const [activeTimePeriod, setActiveTimePeriod] = useState<TimePeriod>(
    TimePeriod.ONE_WEEK
  );

  const dateRange = useDateRange();

  const { locations } = useGetLocationsCached();

  const {
    location,
    handleLocationChange,
    tagLocations,
    handleTagLocationsChange,
  } = useLocationsFilterDropdown();

  const {
    series,
    options,
    loading,
    firstDay,
    showNoData,
    convertInstances,
    totalEmissionsCount,
    refetchWithSpecificData,
    refetchWithChosenValues,
    refetchChartData,
  } = useGasLeakEmissionsChart(
    activeTimePeriod,
    activeStep,
    location?.value
      ? [location.value]
      : tagLocations?.map(l => l?.value ?? "") ?? null
  );

  useEffect((): void => {
    if (deviceData && activeStep === 2) {
      refetchWithSpecificData(deviceData);
    }
  }, [dateRange]);

  useEffect((): void => {
    const attachDate = new Date();

    if (activeTimePeriod === TimePeriod.ONE_MONTH) {
      attachDate.setDate(attachDate.getDate() - 30);
    }

    if (activeTimePeriod === TimePeriod.ONE_WEEK) {
      attachDate.setDate(attachDate.getDate() - 7);
    }

    if (activeTimePeriod === TimePeriod.ONE_DAY) {
      attachDate.setDate(attachDate.getDate() - 1);
    }

    if (activeTimePeriod !== TimePeriod.ALL_TIME) {
      dateRangeVariable({
        from: new Date(attachDate),
        to: new Date(),
      });
    }
  }, [activeTimePeriod]);

  useEffect((): void => {
    if (
      activeTimePeriod === TimePeriod.ALL_TIME &&
      firstDay &&
      activeStep === GasLeakContainerSteps.DetailedLocaleInformation
    ) {
      if (dateRange?.from?.toString() !== new Date(firstDay).toString()) {
        dateRangeVariable({
          from: new Date(firstDay),
          to: new Date(),
        });
      }
    }
  }, [loading, activeStep]);

  useEffect(() => {
    return () => {
      if (dateRange?.from || dateRange?.to) {
        dateRangeVariable({
          from: null,
          to: null,
        });
      }
    };
  }, [dateRange]);

  const handleTimePeriodChange = (timePeriod: TimePeriod): void => {
    if (timePeriod !== activeTimePeriod) {
      setActiveTimePeriod(timePeriod);
    }
  };

  const setLocationFilter = (locationValue: AutocompleteOptionType): void => {
    if (locationValue?.value !== location?.value) {
      handleLocationChange(locationValue, activeTimePeriod, refetchChartData);
    }
  };

  const setTagFilter = (locations: AutocompleteOptionType[] | null): void => {
    if (!isEqual(locations, tagLocations)) {
      handleTagLocationsChange(locations, activeTimePeriod, refetchChartData);
    }
  };

  const setStepsHandler = (step: GasLeakContainerSteps): void => {
    if (
      activeStep === GasLeakContainerSteps.PolarAreaChart &&
      step === GasLeakContainerSteps.DetailedLocaleInformation
    ) {
      refetchWithChosenValues();
    }

    setActiveStep(step);
  };

  const setDeviceHandler = (device: IStatisticsResponseModel): void => {
    setDeviceData(device);

    refetchWithSpecificData(device);
  };

  const locationName = locations?.find(
    (l): boolean => l?.id === deviceData?.location
  )?.name;

  const locationText =
    (deviceData && `${locationName}. ${deviceData.deviceName}`) ?? "";

  return (
    <SimplePaperWrapper
      sx={{
        overflowY:
          activeStep === GasLeakContainerSteps.DetailedLocaleInformation
            ? "auto"
            : "visible",
        padding: "1.5em",
        width: "100%",
        borderRadius: "8px",
        height: CHART_CONTAINER_HEIGHT,
      }}
    >
      <Box
        sx={{
          display:
            activeStep === GasLeakContainerSteps.EmissionVolumeChart
              ? "block"
              : "none",
        }}
      >
        <GasLeakEmissionVolumeView
          showNoData={showNoData}
          activeTimePeriod={activeTimePeriod}
          handleTimePeriodChange={handleTimePeriodChange}
          locationVariable={location}
          handleLocationChange={setLocationFilter}
          handleTagLocationsChange={setTagFilter}
          loading={loading}
          setStepsHandler={setStepsHandler}
          options={options}
          series={series}
        />
      </Box>

      <Box
        sx={{
          display:
            activeStep === GasLeakContainerSteps.DetailedLocaleInformation
              ? "block"
              : "none",
        }}
      >
        <DetailedInformationView
          loading={loading}
          showNoData={showNoData}
          locationVariable={location}
          handleLocationChange={setLocationFilter}
          setDeviceHandler={setDeviceHandler}
          setStepsHandler={setStepsHandler}
          convertInstances={convertInstances}
        />
      </Box>

      <Box>
        {activeStep === GasLeakContainerSteps.PolarAreaChart && (
          <PolarAreaChart
            loading={loading}
            showNoData={showNoData}
            totalEmissionsCount={totalEmissionsCount}
            setStepsHandler={setStepsHandler}
            locationText={locationText}
            options={options}
            series={series}
          />
        )}
      </Box>
    </SimplePaperWrapper>
  );
};

const GasLeakChartContainer = memo(GasLeakChartContainerComponent);

export default GasLeakChartContainer;
